import {createContext, useState, useEffect, useMemo} from 'react'
import type React from 'react'

import {useSearchParams} from '@github-ui/use-navigate'

export const PLAN_TYPES = {
  Individual: 'individual',
  Business: 'business',
} as const

export type PlanType = (typeof PLAN_TYPES)[keyof typeof PLAN_TYPES]

type PlanTypeContextValue = {
  planType: PlanType
  setPlanType: (type: PlanType) => void
}

export const PlanTypeContext = createContext<PlanTypeContextValue>({
  planType: PLAN_TYPES.Individual,
  setPlanType: () => {},
})

type Props = {
  children: React.ReactNode
}

function coercePlanType(value: string | null): PlanType {
  return value === PLAN_TYPES.Business ? PLAN_TYPES.Business : PLAN_TYPES.Individual
}

export function PlanTypeContextProvider(props: Props) {
  const {children} = props

  const [searchParams] = useSearchParams()
  const initialPlanType = coercePlanType(searchParams.get('plans'))
  const [planType, setPlanType] = useState<PlanType>(initialPlanType)

  useEffect(() => {
    setPlanType(coercePlanType(searchParams.get('plans')))
  }, [searchParams])

  const contextValue = useMemo(() => ({planType, setPlanType}), [planType])

  return <PlanTypeContext.Provider value={contextValue}>{children}</PlanTypeContext.Provider>
}

try{ PlanTypeContext.displayName ||= 'PlanTypeContext' } catch {}
try{ PlanTypeContextProvider.displayName ||= 'PlanTypeContextProvider' } catch {}